import { Component, Inject, inject, Input, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OrderSummaryComponent } from '../../ui/order-summary/order-summary.component';
import { CartStateService, PaymentsService } from '@equine-ui-nx/payments';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { AchAcceptanceComponent } from '../../ui/ach-acceptance/ach-acceptance.component';
import { Account, AchAccountsComponent } from '../../ui/ach-accounts/ach-accounts.component';
import { UPDATE_USER } from '@equine-ui-nx/injection-tokens';

interface DwollaLink {
  dwollaCustomerUrl: string,
  id: string,
  fundingSources: string[],
  clientId: string,
  customerFundingSources: Account[]
}


@Component({
  standalone: true,
  selector: 'equine-ui-nx-pay-with-ach',
  templateUrl: './pay-with-ach.component.html',
  styleUrls: ['./pay-with-ach.component.scss'],
  imports: [
    MatButton,
    MatCardModule,
    MatProgressSpinnerModule,
    OrderSummaryComponent,
    MatInputModule,
    ReactiveFormsModule,
    AchAcceptanceComponent,
    AchAccountsComponent
  ],
  providers: [
    PaymentsService,
    MatSnackBar
  ],
})
export class PayWithAchComponent implements OnInit, OnDestroy {
  
  @Input() error!: string;  // This input is for the url error after visa
  @Input() exchange!: string;  // This input is from the url exchange response
  
  private paymentsService = inject(PaymentsService);
  private cartId: string | undefined;
  private router = inject(Router);
  private _snackBar = inject(MatSnackBar);
  private cartState = inject(CartStateService);
  private fb = inject(FormBuilder);

  cartReference = this.paymentsService.getLoggedInUserId() || '';

  cardFields: any;

  orders = this.cartState.ordersForSum;
  subTotal = this.cartState.subtotal;
  totalItems = this.cartState.totalItems;
  donationOrder = this.cartState.donationOrder;
  donationOrderTotal = this.cartState.donationOrderTotal;
  total = this.cartState.total;
  coupon = this.cartState.coupon;

  loading = signal(true);
  cartCompleted = signal(false);
  cartError = signal(null);
  needsAcceptance = signal (true);

  dwollaLink!: DwollaLink;

  cartIntent = 'CAPTURE';

  paying = signal(false);

  errors: WritableSignal<{key: string, value: string}[]> = signal<{key: string, value: string}[]>([]);

  linkLoading = signal<boolean>(true);
  firstClickAfterAdd = signal<boolean>(false);
  errorAfterExchange = signal<boolean>(false);
  exchangeToken = signal<string | null>(null);

  unsubscribe$ = new Subject<void>();

  bankAccountType!: FormControl;
  nickName!: FormControl;

  constructor(
    @Inject(UPDATE_USER) private updateUserToken: BehaviorSubject<void>
  ) { }

  ngOnInit() {
    this.getAmount();

    if (this.error) {
      this.errorAfterExchange.set(true);
    }

    if (this.exchange) {

      this.bankAccountType = this.fb.control(null, Validators.required);
      this.nickName = this.fb.control('', {validators: [Validators.required, Validators.minLength(6), Validators.maxLength(49)]})

      this.exchangeToken.set(this.exchange);
    }

  }

  getAmount() {
    this.loading.set(true);
    
    this.paymentsService.getCartSummary(undefined, this.cartReference)
      .subscribe({
        next: (response) => {
          this.cartIntent = response?.cart?.intent || 'CAPTURE';
          this.cartId = response.cart.id;
          this.loading.set(false);
          if (response.status === 'COMPLETE') {
            this.cartCompleted.set(true);
          }
          this.loadDwollaLink()
        },
        error: (error) => {
          const errorMessage = error?.error?.message || 'An error ocurred. Cannot load cart'
          this.cartError.set(errorMessage);
          this.loading.set(false);
        }
      });
  }

  loadDwollaLink() {
    this.linkLoading.set(true);
    this.paymentsService.getDwollaLinkFromClientId(this.cartReference)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.linkLoading.set(false);
          if(response.linkNotFound) {
            this.presentAcceptanceStep();
          }

          if (response.linkFound) {
            this.dwollaLink = response;

            if (response.needsAcceptance) {
              this.presentAcceptanceStep();
            } else {
              this.needsAcceptance.set(false);
              if (!response.dwollaCustomerUrl) {
                this.createCustomer();
              }
            }
          }
        },
        error: (error) => {
          this.linkLoading.set(false);
          this._snackBar.open(`An error occured. Please refresh`, 'Dismiss');
        }
      })
  }

  presentAcceptanceStep() {
    this.needsAcceptance.set(true);
  }

  acceptTerms() {
    this.paymentsService.createOrUpdateDwollaLinkWithAcceptance(this.paymentsService.getLoggedInUserId() as string)
      .pipe(
        takeUntil(this.unsubscribe$)
      )  
      .subscribe({
        next: (response) => {
          this.needsAcceptance.set(false);
          this.loadDwollaLink();
        },
        error: () => {
          this._snackBar.open(`Error: fail creating link`, 'Dismiss')
        }
      });
  }

  createCustomer() {
    const customerData = this.paymentsService.getLoggedInUserDataForDwolla();

    if (!customerData) {
      this._snackBar.open(`Error: customer data not found`, 'Dismiss');
    }

    this.paymentsService.createDwollaCustomer(customerData as {firstName: string, lastName: string, customerId: string, email: string}, )
      .subscribe({
        next: (response) => {
          this.dwollaLink = {...this.dwollaLink, ...response};
        },
        error: (err) => {
          console.error(err)
        }
      })
    

  }

  confirmAddNewAccount() {
    this.firstClickAfterAdd.set(true);
  }

  cancelAddAccount() {
    this.firstClickAfterAdd.set(false);
  }

  createExchangeSession() {
    this.loading.set(true);
    this.paymentsService.createExchangeSession(this.dwollaLink.dwollaCustomerUrl)
      .subscribe({
        next: (response) => {
          window.location.href = response.redirect;
        },
        error: (err) => {
          console.error(err)
        }
      });
  }


  addNewAccount() {
    if (!this.bankAccountType.value) {
      this._snackBar.open(`Bank account type needs to be selected`, 'Dismiss');
    }

    this.loading.set(true);
    this.paymentsService.addNewAccountWithExchange(this.dwollaLink.id, this.exchangeToken() as string, this.bankAccountType.value, this.nickName.value)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.removeQueryParams();
          this.loading.set(false);
          this.loadDwollaLink();
          this.exchangeToken.set(null);
        },
        error: (error: any) => {
          this._snackBar.open(`Error adding account: ${error.message}`, 'Dismiss').afterDismissed().subscribe({
            next: () => {
              this.tryAgainAfterError();
            }
          });
        }
      })
  }

  tryAgainAfterError() {
    this.removeQueryParams();
    this.loadDwollaLink();
    this.errorAfterExchange.set(false);
  }

  payWithAccount(event: {id: string}) {
    this.paying.set(true);
    this.paymentsService.captureWithAch(this.cartId as string, this.dwollaLink.id, event.id)
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (success) => {
          this.paymentsService.setCartAsEmptyAfterPayment();
          this.fetchUserIfNeeded(success);
          this._snackBar.open(`Success!`, 'Dismiss', {
            duration: 5000,
            panelClass: ['success-snackbar'],
          });
          this.router.navigate(['payments', 'success']);
        },
        error: (error) => {
          this.paying.set(false);

          if (error?.error?.message) {
            this._snackBar.open(`Error: ${error.error.message}`, 'Dismiss')
          } else {
            this._snackBar.open(`Error: an error occured. Please try again`, 'Dismiss')
          }
        }
      })
  } 
  
  removeAccount(event: {id: string}) {
    this.loading.set(true);
    this.paymentsService.removeAccount(this.dwollaLink.id, event.id)
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (success) => {
          this.loadDwollaLink();
          this.loading.set(false);
          this._snackBar.open(`Success!`, 'Dismiss', {
            duration: 5000,
            panelClass: ['success-snackbar'],
          });
        },
        error: (error) => {
          this.loading.set(false);
          if (error?.error?.message) {
            this._snackBar.open(`Error: ${error.error.message}`, 'Dismiss')
          } else {
            this._snackBar.open(`Error: an error occured. Please try again`, 'Dismiss')
          }
        }
      })
  }

  fetchUserIfNeeded(successResponse: any) {
      this.updateUserToken.next();
  }

  onSubmit() {
    
  }

  removeQueryParams() {
    this.router.navigate([], {
      queryParams: {}
    });
  }

  goBack() {
    this.router.navigate(['payments', 'payment-summary'])
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
